import { invariant } from '@seen/core/invariant'
import type { ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, useEffect, useRef } from 'react'
import {
  ObservationChoiceEditor,
  ObservationReferenceEditor,
  ObservationTextEditor,
} from '#src/components/rich-text-editor/ObservationNode/ObservationBoundEditors.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'

// The node's pickers open outside it, in a portal: a click in one is still
// work on this observation. (Its details dialog holds the editor open
// through `held`; any other dialog, such as one the note itself sits in,
// does not count.)
const PORTALLED = '[data-radix-popper-content-wrapper]'

export interface ObservationInlineEditorProps {
  observation: ExtendedObservation
  attributes: ObservationNodeAttributes
  // Stay open while the details dialog is, since closing it returns here.
  held: boolean
  onDone: () => void
  onDetailsOpenChange: (open: boolean) => void
  // The node's own, where it can be cleared back to unanswered.
  updateAttributes?: (attributes: Partial<ObservationNodeAttributes>) => void
}

// A recorded observation opened for changes where it sits in the note. It
// closes again on Escape or a click anywhere else; the date, history and
// delete stay one step further away, behind the details button.
export function ObservationInlineEditor({
  observation,
  attributes,
  held,
  onDone,
  onDetailsOpenChange,
  updateAttributes,
}: ObservationInlineEditorProps): JSX.Element {
  const { scaleType, referenceResourceType } = attributes
  const wrapperRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (held) {
      return
    }
    const inNode = (target: EventTarget | null): boolean =>
      target instanceof Node && !!wrapperRef.current?.contains(target)
    const onPointerDown = (event: PointerEvent): void => {
      const { target } = event
      // While a modal layer (a picker's list) is open, Radix turns off
      // pointer events outside it, so a click meant for the note lands on the
      // body. That click closes the layer, not this editor.
      if (
        document.body.style.pointerEvents === 'none' ||
        target === document.body ||
        target === document.documentElement
      ) {
        return
      }
      // A press on what is no longer in the page started before the editor
      // replaced it: the gesture that opened the editor, not one away from it.
      if (target instanceof Node && !target.isConnected) {
        return
      }
      if (
        !inNode(target) &&
        !(target instanceof Element && target.closest(PORTALLED))
      ) {
        onDone()
      }
    }
    // Only for a key pressed inside the node, so Escape in an open picker
    // closes just the picker. Caught on the way down: a key event from the
    // fields inside did not bubble as far as the node when tested.
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && inNode(event.target)) {
        onDone()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [held, onDone])

  // Focus starts inside the node, so the details button and an empty note,
  // which show only while it holds focus, are there at once. The text editor
  // focuses itself.
  useEffect(() => {
    if (scaleType !== 'Nar') {
      wrapperRef.current?.querySelector<HTMLElement>('button')?.focus()
    }
  }, [scaleType])

  const shared = {
    observation,
    attributes,
    wrapperRef,
    onDetailsOpenChange,
    updateAttributes,
  }

  if (scaleType === 'Ref') {
    invariant(referenceResourceType, 'referenceResourceType is required')
    return (
      <ObservationReferenceEditor
        {...shared}
        resourceType={referenceResourceType}
      />
    )
  }

  if (scaleType === 'Nar') {
    return <ObservationTextEditor {...shared} autofocus />
  }

  invariant(
    scaleType === 'Ord' || scaleType === 'Nom',
    'scaleType must be Ord, Nom, Nar or Ref'
  )
  return (
    <ObservationChoiceEditor
      {...shared}
      answerOptions={attributes.answerOptions ?? []}
      answerValueSet={attributes.answerValueSet ?? null}
      scaleType={scaleType}
    />
  )
}
